/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import { Card, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  SelectInput,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { Pagination } from "../../../components/Pagination";
import { ChevronRightIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { Empty } from "../../../components/common/Empty";
import { SearchField } from "../../../components/common/SearchField";
import { isSubscriptionDisabled } from "../../../utils/appUtils";
import { UserTypeOptions } from "../../../utils/constants";
import { UserStatusIcon } from "./UserStatusIcon";
import { useSearchableList } from "../../../dataProvider/useSearchableList";

const useStyles = makeStyles((theme) => ({
  statusIcon: {
    marginTop: theme.spacing(1),
  },
}));

const renderName = (record) => {
  if (record?.user) return `${record.user.lastName}, ${record.user.firstName}`;
  return `${record.lastName}, ${record.firstName}`;
};

const UserSearchField = ({ label, alwaysOn, ...props }) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;

      const filters = {
        ...nextFilterValues,
        or: [
          { firstName: { matchPhrasePrefix: `${value}` } },
          { firstName: { wildcard: `*${value}*` } },
          { lastName: { matchPhrasePrefix: `${value}` } },
          { lastName: { wildcard: `*${value}*` } },
          { email: { matchPhrasePrefix: `${value}` } },
          { email: { wildcard: `*${value}*` } },
          { phone: { matchPhrasePrefix: `${value}` } },
          { phone: { wildcard: `*${value}*` } },
        ],
      };
      setFilters(filters, displayedFilters);
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (gteMedium) => {
  return [
    <SelectInput
      source="type.eq"
      label="Role"
      key="type-filter"
      variant="outlined"
      resettable
      alwaysOn={gteMedium}
      choices={UserTypeOptions || []}
      optionValue="id"
    />,
    <UserSearchField label="Search" alwaysOn />,
  ];
};

const ListActions = ({ isXSmall, ...props }) => {
  return (
    <TopToolbar>
      {!isXSmall && <FilterButton />}
      <CreateButton disabled={isSubscriptionDisabled()} />
      <RefreshButton />
    </TopToolbar>
  );
};

export const UserList = (props) => {
  const classes = useStyles();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const lteMedium = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const gteMedium = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const [defaultFilter] = useState({
    reloadYardId: window.appUser?.reloadYardId,
    activeLastName: { beginsWith: { active: "true" } },
  });

  const defaultSearchFilter = {
    reloadYardId: { eq: window.appUser?.reloadYardId },
    active: { eq: "true" },
  };

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"userByActiveByLastName", "search":"lastName"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Users",
    defaultFilter: defaultFilter,
    defaultSort: defaultSort,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    resource: "users",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Users" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(gteMedium)}
        actions={<ListActions isXSmall={isXSmall} />}
      />
      <Card>
        <StaffGrid
          {...props}
          {...listContext}
          isXSmall={isXSmall}
          lteMedium={lteMedium}
          classes={classes}
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const StaffGrid = ({ ids, page, ...props }) => {
  const { classes, isXSmall, lteMedium } = props;

  if (ids.length === 0 && page === 1)
    return <Empty {...props} message="Create a new user" />;
  return isXSmall ? (
    <SimpleList
      primaryText={(record) => record.fullName}
      secondaryText={(record) => record.email}
      tertiaryText={(record) => record.type}
      linkType="edit"
      leftIcon={(record) => <UserStatusIcon record={record} />}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
    />
  ) : lteMedium ? (
    <Datagrid rowClick="edit">
      <FunctionField
        source="lastName"
        label="Name"
        render={renderName}
        sortable={true}
        sortBy={`{"key":"userByActiveByLastName", "search":"lastName"}`}
      />
      <TextField source="type" label="User Role" sortable={true} />
      <UserStatusIcon
        source="status"
        label="Status"
        classes={classes.statusIcon}
        sortable={false}
      />
      <TextField source="email" label="Email" sortable={true} />
    </Datagrid>
  ) : (
    <Datagrid rowClick="edit">
      <FunctionField
        source="lastName"
        label="Name"
        render={renderName}
        sortable={true}
        sortBy={`{"key":"userByActiveByLastName", "search":"lastName"}`}
      />
      <TextField source="type" label="User Role" sortable={true} />
      <UserStatusIcon
        source="status"
        label="Status"
        classes={classes.statusIcon}
        sortable={false}
      />
      <TextField source="email" label="Email" sortable={true} />
      <TextField source="phone" label="Phone" sortable={true} />
      <EditButton />
    </Datagrid>
  );
};
